import { BenefitType } from '@zorro/clients';
import { Accordion } from '@zorro/design-system/v2/Accordion';
import { Button } from '@zorro/design-system/v2/Button';
import { Pill } from '@zorro/design-system/v2/Pill';
import { ThemeIcon } from '@zorro/design-system/v2/ThemeIcon';
import { Group, Icon, Stack, Text } from '@zorro/web/design-system';
import {
  MonolithMethodNames,
  MonolithMethods,
  UseMonolithQueryProps,
} from '@zorro/web/ui-utils';
import { ElementType, useState } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import { PlanElectionFormInputs } from './PlanElectionFormInputs';
import {
  PlanElectionFormFields,
  PlanElectionItem,
  PlanElectionItemFormFields,
  mapBenefitTypeToPlanElectionFlow,
} from './PlanElectionFormUtils';

type Props<T extends MonolithMethodNames> = {
  method: T;
  getParams: (whoIsEnrolled: string[]) => UseMonolithQueryProps<T>['params'];
  mapDataToPlanElectionItem: (
    data?: Awaited<ReturnType<MonolithMethods[T]>>
  ) => PlanElectionItem[] | undefined;
  getDefaultPlanElectionFormFields: () => PlanElectionItemFormFields;
  planElectionForm: UseFormReturn<PlanElectionFormFields>;
  insuredIdToNameMap: Map<string, string>;
  onDownloadPlans: (data?: Awaited<ReturnType<MonolithMethods[T]>>) => void;
  whoIsEnrolledOptions: string[];
  whoIsEnrolledInMultiplePlans: string[];
  benefitType: BenefitType;
  isCombinedPlanStructure?: boolean;
  icon: ElementType;
  title: string;
  addPlanButtonText: string;
};

export const PlanElectionForm = <T extends MonolithMethodNames>({
  getDefaultPlanElectionFormFields,
  isCombinedPlanStructure = false,
  whoIsEnrolledInMultiplePlans,
  mapDataToPlanElectionItem,
  insuredIdToNameMap,
  addPlanButtonText,
  planElectionForm,
  whoIsEnrolledOptions,
  onDownloadPlans,
  benefitType,
  getParams,
  method,
  icon,
  title,
}: Props<T>) => {
  'use no memo';
  const [openedPlans, setOpenedPlans] = useState<string[]>(['0']);
  const { fields, remove, append } = useFieldArray({
    control: planElectionForm.control,
    name: 'plans',
  });
  const { sendEvent } = useAnalytics();

  const handleAppendPlan = async () => {
    append(getDefaultPlanElectionFormFields(), { shouldFocus: false });
    setOpenedPlans((prev) => [...prev, fields.length.toString()]);

    await sendEvent('manual_election_plan_added', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      plan_count: fields.length + 1,
    });
  };

  const handleOpenedPlansChange = async (updatedOpenedPlans: string[]) => {
    setOpenedPlans(updatedOpenedPlans);

    const toggledPlan =
      updatedOpenedPlans.find((value) => !openedPlans.includes(value)) ??
      openedPlans.find((value) => !updatedOpenedPlans.includes(value));

    if (!toggledPlan) {
      return;
    }

    await sendEvent('manual_election_plan_row_toggled', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      is_opened: updatedOpenedPlans.includes(toggledPlan),
      plan_index: Number(toggledPlan),
    });
  };

  const handleRemovePlan = async (index: number) => {
    remove(index);

    await sendEvent('manual_election_plan_removed', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      plan_count: fields.length - 1,
      plan_index: index,
    });
  };

  return (
    <Stack>
      <Accordion
        multiple
        value={openedPlans}
        onChange={handleOpenedPlansChange}
        styleVariant="shadow"
      >
        {fields.map(({ id }, index) => (
          <Accordion.Item key={id} value={index.toString()}>
            <Accordion.Control>
              <Group>
                {icon && (
                  <ThemeIcon size="48" color="indigo">
                    <Icon icon={icon} />
                  </ThemeIcon>
                )}
                <Text fw="600">
                  {isCombinedPlanStructure
                    ? 'Combined plan'
                    : `${title} ${index + 1}`}
                </Text>
                {planElectionForm.formState.errors?.plans?.[index] && (
                  <Pill variant="tag" color="red">
                    Missing details
                  </Pill>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <PlanElectionFormInputs
                mapDataToPlanElectionItem={mapDataToPlanElectionItem}
                whoIsEnrolledInMultiplePlans={whoIsEnrolledInMultiplePlans}
                isCombinedPlanStructure={isCombinedPlanStructure}
                onDeleteDependent={() => handleRemovePlan(index)}
                insuredIdToNameMap={insuredIdToNameMap}
                hasMoreThanOnePlan={fields.length > 1}
                whoIsEnrolledOptions={whoIsEnrolledOptions}
                planElectionForm={planElectionForm}
                onDownloadPlans={onDownloadPlans}
                benefitType={benefitType}
                source="manual_election"
                getParams={getParams}
                method={method}
                index={index}
              />
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
      {!isCombinedPlanStructure && (
        <Button
          leftSection={<Icon icon={icon} />}
          onClick={handleAppendPlan}
          variant="secondary"
          size="lg"
          w="50%"
        >
          {addPlanButtonText}
        </Button>
      )}
    </Stack>
  );
};
